import { Box, Flex, MenuItemProps, TagBlock } from '@televet/kibble-ui';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { ActionEffectType, CreateTaskActionEffect } from '@televet/shared-types/JsonTypes/WorkflowEventActionConfig';
import { Dictionary } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useGetClinicUsersQuery } from 'shared/types/graphql';

type UserMap = Dictionary<{
  id: string;
  label: string;
}>;
interface CreateTaskFormProps {
  taskCreationEffectData?: CreateTaskActionEffect;
  onCreateTaskEffectChange: (actionEffect: CreateTaskActionEffect) => void;
  errors?: Record<string, boolean>;
}

const CreateTaskForm = ({
  onCreateTaskEffectChange,
  taskCreationEffectData = { type: ActionEffectType.CreateTask, assigneeIds: [], taskName: '' },
  errors,
}: CreateTaskFormProps): JSX.Element => {
  const { currentClinicId } = useClinicUser();

  const { data: usersData, loading } = useGetClinicUsersQuery({
    variables: { clinicId: currentClinicId || '' },
    skip: !currentClinicId,
    fetchPolicy: GraphQLFetchPolicies.CacheFirst,
  });

  const { userMap, menuItems } = useMemo(() => {
    return (usersData?.users || []).reduce(
      (args, user) => {
        const label = `${user.firstName} ${user.lastName}`;

        args.userMap[user.id] = {
          id: user.id,
          label,
        };

        args.menuItems.push({
          value: user.id,
          label,
          isSelected: (taskCreationEffectData?.assigneeIds || []).some((selectedId) => selectedId === user.id),
        });

        return args;
      },
      {
        userMap: {},
        menuItems: [],
      } as {
        userMap: UserMap;
        menuItems: MenuItemProps[];
      },
    );
  }, [taskCreationEffectData.assigneeIds, usersData?.users]);

  const selectedAssigneeTags = useMemo(
    () =>
      taskCreationEffectData.assigneeIds.map((id) => ({
        label: userMap[id]?.label || '(Inactive user)',
        value: id,
      })),
    [taskCreationEffectData.assigneeIds, userMap],
  );

  const handleAllSelection = useCallback(
    (selectedItems: MenuItemProps[]): void => {
      onCreateTaskEffectChange({
        ...taskCreationEffectData,
        assigneeIds: selectedItems.map((i) => i.value || ''),
      });
    },
    [taskCreationEffectData, onCreateTaskEffectChange],
  );

  const handleSelection = useCallback(
    (selectedItem: MenuItemProps, selectedItems?: MenuItemProps[]): void => {
      handleAllSelection(selectedItems || []);
    },
    [handleAllSelection],
  );

  const handleRemove = useCallback(
    (value: string): void => {
      onCreateTaskEffectChange({
        ...taskCreationEffectData,
        assigneeIds: taskCreationEffectData.assigneeIds.filter((id) => id !== value),
      });
    },
    [onCreateTaskEffectChange, taskCreationEffectData],
  );

  const handleTextChange = useCallback(
    (e): void =>
      onCreateTaskEffectChange({
        ...taskCreationEffectData,
        taskName: e.target.value,
      }),
    [onCreateTaskEffectChange, taskCreationEffectData],
  );

  return (
    <Box className="Automations__CreateTaskForm" w="100%">
      <TextInput
        name="name"
        placeholder="Task Name"
        errorText="Please enter a task name"
        isInvalid={!!errors?.taskName}
        w="50%"
        minW="235px"
        value={taskCreationEffectData.taskName}
        onChange={handleTextChange}
        label="Task Name"
        data-testid="task-name-input"
      />
      <Flex align="center" gap={2} mt={3} w="100%">
        <Box w="100%">
          <TagBlock
            dataTestId="assignees-tag-block"
            title="Assignees"
            tags={selectedAssigneeTags}
            menuContainerProps={{
              strategy: 'fixed',
            }}
            isLoading={loading}
            menuListProps={{
              isMultiSelect: true,
              menuItems,
              onSelect: handleSelection,
              isSearchable: true,
              canSelectAll: true,
              onSelectAll: handleAllSelection,
            }}
            onClearAll={(): void => handleAllSelection([])}
            onRemoveTag={handleRemove}
          />
        </Box>
      </Flex>
    </Box>
  );
};

export default CreateTaskForm;
